// src/hooks/useNotifications.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useEffect } from 'react';
import drEasyApi from '../Api/Customapi';

export const useNotifications = () => {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const res = await drEasyApi.get('/api/Notification/GetMyNotifications');
      return res.data;
    },
    // Remove automatic refetching - we'll control this manually
    refetchInterval: false,
    refetchIntervalInBackground: false, 
    refetchOnWindowFocus: false,
    // Keep data fresh for longer
    staleTime: 30000, // 30 seconds
    gcTime: 300000, // 5 minutes
  });
};

export const useUnreadCount = () => {
  const queryClient = useQueryClient();
  const previousCount = useRef(null);

  const query = useQuery({
    queryKey: ['unreadCount'],
    queryFn: async () => {
      const res = await drEasyApi.get('/api/Notification/GetUnreadCount');
      return res.data.unreadCount;
    },
    // Increase interval to reduce API calls
    refetchInterval: 15000, // 15 seconds instead of 5
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true, // Keep this for better UX when user returns
    staleTime: 10000, // 10 seconds
    gcTime: 300000, // 5 minutes
  });

  // Watch for changes in unread count
  useEffect(() => {
    if (query.data !== undefined && query.data !== previousCount.current) {
      // Only refetch notifications if count actually changed
      if (previousCount.current !== null && query.data !== previousCount.current) {
        console.log(`Unread count changed from ${previousCount.current} to ${query.data}, refetching notifications...`);
        queryClient.invalidateQueries(['notifications']);
      }
      previousCount.current = query.data;
    }
  }, [query.data, queryClient]);

  return query;
};

export const useMarkAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) =>
      drEasyApi.put(`/api/Notification/MarkAsRead/${id}`),
    onSuccess: () => {
      // Invalidate both queries to ensure consistency
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['unreadCount']);
    },
  });
};

export const useMarkAllAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => drEasyApi.put(`/api/Notification/MarkAllAsRead`),
    onSettled: () => {
      // Force refetch even if success or error
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['unreadCount']);
    },
  });
};

// New hook for opening notification (calling the OpenNotification endpoint)
export const useOpenNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id) =>
      drEasyApi.put(`/api/Notification/OpenNotfication/${id}`),
    onSuccess: () => {
      // Invalidate both queries to ensure consistency
      queryClient.invalidateQueries(['notifications']);
      queryClient.invalidateQueries(['unreadCount']);
    },
  });
};

// Optional: Custom hook for manual notification refresh
export const useRefreshNotifications = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries(['notifications']);
    queryClient.invalidateQueries(['unreadCount']);
  };
};